import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

/**
 * The only way a page changes data (Conventions/06 §5).
 *
 * Name every query your change affects and they are refreshed on success. A mutation that forgets one key
 * leaves a page showing a stale list until a full reload; making the list an argument makes forgetting it
 * visible in review.
 */
export function useAppMutation<TResult, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  invalidates: Array<string>,
  onSuccess?: (result: TResult) => void,
): UseMutationResult<TResult, Error, TVariables> {
  const queryClient = useQueryClient();

  return useMutation<TResult, Error, TVariables>({
    mutationFn,
    onSuccess: (result: TResult): void => {
      for (const key of invalidates) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }

      onSuccess?.(result);
    },
  });
}
